import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { captureEvent } from '../../../services/event-capture';
import {
  mapBaseLayerDefinitions,
  updateBaseLayer,
} from '../../../services/map-style';
import { selectSelectedBaseMapLayer } from '../../../services/store/modules/app-controls';
import { selectHasCurrentMemberActiveSubscription } from '../../../services/store/modules/backend-service';
import {
  getSelectedLayers,
  switchLayer,
} from '../../../services/store/modules/map-settings';
import usePaywall from '../../hooks/use-paywall';
import MapSettingsContainerView from './components/MapSettingsContainerView';
import { BaseMapDefinition, isSubscriberOnlyLayer } from './mapSettingsConfig';

// `services/map-style` is plain JS, so its export widens to a literal object.
const baseMapDefinitions = mapBaseLayerDefinitions as Record<
  string,
  BaseMapDefinition
>;

const MapSettingsPage: FC = () => {
  const dispatch = useDispatch();

  const selectedBaseMapLayer = useSelector(selectSelectedBaseMapLayer);
  const selectedLayerTypes: string[] = useSelector(getSelectedLayers);
  const hasActiveSubscription = useSelector(
    selectHasCurrentMemberActiveSubscription,
  );

  // One call only. `usePaywall` fetches member settings on mount, so calling
  // it per referrer (as this sheet used to, across two components) duplicates
  // that request every time the sheet opens. Everything gated here now
  // attributes to the sheet rather than the individual layer.
  const { presentPaywall, hasSubscriptionFeaturesEnabled } =
    usePaywall('Map settings');

  const handleSelectBaseMap = (baseMapLayerToSelect: string) => {
    const config = baseMapDefinitions[baseMapLayerToSelect];

    if (config?.subscriberOnly && !hasActiveSubscription) {
      presentPaywall();
      return;
    }

    captureEvent({
      type: 'map: change base layer',
      payload: { baseMapLayerToSelect },
    });
    updateBaseLayer(baseMapLayerToSelect);
  };

  const handleToggleLayer = (layerName: string) => {
    if (isSubscriberOnlyLayer(layerName) && !hasActiveSubscription) {
      presentPaywall();
      return;
    }

    dispatch(
      switchLayer({
        name: layerName,
        enabled: selectedLayerTypes.indexOf(layerName) === -1,
      }),
    );
  };

  return (
    <MapSettingsContainerView
      baseMapDefinitions={baseMapDefinitions}
      selectedBaseMapLayer={selectedBaseMapLayer}
      onSelectBaseMap={handleSelectBaseMap}
      hasSubscriptionFeaturesEnabled={hasSubscriptionFeaturesEnabled}
      selectedLayerTypes={selectedLayerTypes}
      onToggleLayer={handleToggleLayer}
    />
  );
};

export default MapSettingsPage;
